import React from 'react';

import {
    useTable,
    useSortBy,
    useFilters,
    useGlobalFilter,
    usePagination,
    Column,
} from 'react-table';
import matchSorter from 'match-sorter';
import { BrokerApplication } from 'signature-public-api-sdk';
import { tableStyles } from './styles';
import TableContainer from '@material-ui/core/TableContainer';
import Paper from '@material-ui/core/Paper';
import ApplicationsTableHead from '../ApplicationsTableHead';

import ApplicationsTablePagination from '../ApplicationsTablePagination';
import ApplicationsTableBody from '€components/ApplicationsTableBody';

import ApplicationsTableToolbar from '€components/ApplicationsTableToolbar';
import { Divider, Table as MuiTable } from '@material-ui/core';
import ApplicationsTableDefaultColumnFilter from '€components/ApplicationsTableDefaultColumnFilter';

interface TableProps<T extends object> {
    columns: Column<T>[];
    data: T[];
}

export function Table<T extends BrokerApplication>({ columns, data }: TableProps<T>) {
    const classes = tableStyles();

    function fuzzyTextFilter(rows, id, filterValue) {
        return matchSorter(rows, filterValue, { keys: [row => row.values[id]] });
    }

    fuzzyTextFilter.autoRemove = val => !val;

    const filterTypes = React.useMemo(
        () => ({
            fuzzyText: fuzzyTextFilter,
            multiSelect: (rows, id, filterValues) => {
                if (filterValues.length === 0) return rows;
                return rows.filter(r => filterValues.includes(r.values[id].toString()));
            },
            text: (rows, id, filterValue) => {
                return rows.filter(row => {
                    const rowValue = row.values[id];
                    return rowValue !== undefined
                        ? String(rowValue)
                              .toLowerCase()
                              .startsWith(String(filterValue).toLowerCase())
                        : true;
                });
            },
        }),
        [],
    );

    const defaultColumn = React.useMemo(
        () => ({
            Filter: ApplicationsTableDefaultColumnFilter,
        }),
        [],
    );

    const {
        getTableProps,
        getTableBodyProps,
        headerGroups,
        rows,
        prepareRow,
        state,
        preGlobalFilteredRows,
        setGlobalFilter,
        setAllFilters,
        page,
        canPreviousPage,
        canNextPage,
        pageOptions,
        pageCount,
        gotoPage,
        nextPage,
        previousPage,
        setPageSize,
        state: { pageIndex, pageSize },
    } = useTable<T>(
        {
            columns,
            data,
            initialState: { pageIndex: 0, pageSize: 10 },
            defaultColumn,
            filterTypes,
        },
        useFilters,
        useGlobalFilter,
        useSortBy,
        usePagination,
    );

    return (
        <div className={classes.root}>
            <Paper className={classes.paper} elevation={0}>
                <ApplicationsTableToolbar
                    headerGroups={headerGroups}
                    preGlobalFilteredRows={preGlobalFilteredRows}
                    globalFilter={state.globalFilter}
                    setGlobalFilter={setGlobalFilter}
                    rows={rows}
                    setAllFilters={setAllFilters}
                />

                <Divider />
                <TableContainer className={classes.tableContainer}>
                    <MuiTable
                        className={classes.table}
                        aria-labelledby="tableTitle"
                        size="medium"
                        aria-label="Applications table"
                        {...getTableProps()}
                    >
                        <ApplicationsTableHead headerGroups={headerGroups} />
                        <ApplicationsTableBody
                            rows={rows}
                            page={page}
                            getTableBodyProps={getTableBodyProps}
                            prepareRow={prepareRow}
                        />
                    </MuiTable>
                </TableContainer>
                <ApplicationsTablePagination
                    page={page}
                    canPreviousPage={canPreviousPage}
                    canNextPage={canNextPage}
                    pageOptions={pageOptions}
                    pageIndex={pageIndex}
                    pageCount={pageCount}
                    gotoPage={gotoPage}
                    nextPage={nextPage}
                    previousPage={previousPage}
                    pageSize={pageSize}
                    setPageSize={setPageSize}
                    applicationsLength={data.length}
                />
            </Paper>
        </div>
    );
}
